import {filter, Observable, Subject, takeUntil, tap} from 'rxjs';
import {SubscriptionsMap} from '../common/subscriptions-map';
import {EventBus} from './events';
import {Constructor, EventContext, EventHandler, EventManagerMethods} from '../common/types';

/**
 * The instance of class can dispatch and subscribe events to/from EventBus.
 * Require call a 'destroy' method when component will destroy.
 **/
export class EventBusManager implements EventManagerMethods {
	private readonly onDestroy$: Subject<void> = new Subject();
	private subscriptions: SubscriptionsMap = new SubscriptionsMap();

	constructor() {
		EventBus.eventsBus$
			.pipe(
				takeUntil(this.onDestroy$),
				tap((event) => this.subscriptions.get(event.event).forEach(s => s.next(event.args, event.context))),
			)
			.subscribe();
	}


	public unsubscribe<T>(event: any, callback: EventHandler<T>)
	public unsubscribe<T>(event: Function, callback: EventHandler<T>)
	public unsubscribe<T>(event: Constructor<T>, callback: EventHandler<T>) {
		this.subscriptions.remove(event, callback);
	}

	public destroy() {
		this.onDestroy$.next();
		this.onDestroy$.complete();
		this.subscriptions.free();
	}

	public subscribe<T>(event: (...args) => T, callback: Function): void;
	public subscribe<T>(event: (...args) => T, callback: EventHandler<T>): void;
	public subscribe<T>(event: Constructor<T>, callback: EventHandler<T>): void;
	public subscribe<T>(event: T, callback: (event: any) => void): void;
	public subscribe(event: any, callback: any): void {
		this.subscriptions.add(event, callback);
	}

	public dispatch<T>(event: (...args) => T, args: T): void;
	public dispatch<T>(event: Constructor<T>, args?: T): void;
	public dispatch<T>(event: T, args?: any): void;
	public dispatch(event: any, args?: any): void {
		EventBus.dispatch(event, args);
	}

	public fromEvent<T>(event: Constructor<T>): Observable<{args: T; context: EventContext}> {
		return EventBus.eventsBus$.pipe(takeUntil(this.onDestroy$), filter(e => e.event === event));
	}
}
